import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import { Badge } from "@coval/ui/badge"
import { Tooltip } from "@coval/ui/tooltip"

import type {
  MetricRecordData,
  TargetCondition,
} from "@/types/metrics/contracts"

import { deriveYesNoString } from "../utils/binaryValue"
import {
  getMetricColor,
  getMetricColorClass,
  getMetricColorForMetric,
} from "../utils/getMetricColor"
import { yesNoBadgeShapeClasses } from "../utils/yesNoBadgeClasses"

export { deriveYesNoString } from "../utils/binaryValue"

/** Override labels for the YES/NO/UNKNOWN slots. */
export interface YesNoLabels {
  yes?: string
  no?: string
  unknown?: string
}

export const StringValueYesNoChip = ({
  value,
  mo,
  targetCondition,
  showDash = false,
  labels,
  metric,
  color: colorOverride,
}: {
  value?: string | null
  mo?: { float_value?: number | null; string_value?: string | null }
  targetCondition?: TargetCondition
  showDash?: boolean
  labels?: YesNoLabels
  metric?: MetricRecordData
  color?: ReturnType<typeof getMetricColor>
}) => {
  const resolvedValue = mo ? deriveYesNoString(mo) : value

  if (resolvedValue === null || resolvedValue === undefined) {
    return null
  }

  if (
    resolvedValue !== ANNOTATION_VALUE.YES &&
    resolvedValue !== ANNOTATION_VALUE.NO &&
    resolvedValue !== ANNOTATION_VALUE.UNKNOWN
  ) {
    return <div>N/A</div>
  }

  const color =
    colorOverride ??
    (metric
      ? getMetricColorForMetric(resolvedValue, metric)
      : getMetricColor(resolvedValue, targetCondition))
  const colorClass = getMetricColorClass(color)
  const yesLabel = labels?.yes ?? "Yes"
  const noLabel = labels?.no ?? "No"
  const unknownLabel = labels?.unknown ?? "N/A"
  return (
    <div className="flex items-center justify-center h-full">
      {resolvedValue === ANNOTATION_VALUE.UNKNOWN &&
        (showDash ? (
          <Badge size="chip" className={`h-[28px] text-sm ${colorClass}`}>
            --
          </Badge>
        ) : (
          <Tooltip content={unknownLabel}>
            <Badge size="chip" className={`h-[28px] text-sm ${colorClass}`}>
              {unknownLabel}
            </Badge>
          </Tooltip>
        ))}
      {resolvedValue === ANNOTATION_VALUE.YES && (
        <Tooltip content={yesLabel}>
          <Badge
            size="chip"
            className={`${yesNoBadgeShapeClasses} ${colorClass}`}
          >
            {yesLabel}
          </Badge>
        </Tooltip>
      )}
      {resolvedValue === ANNOTATION_VALUE.NO && (
        <Tooltip content={noLabel}>
          <Badge
            size="chip"
            className={`${yesNoBadgeShapeClasses} ${colorClass}`}
          >
            {noLabel}
          </Badge>
        </Tooltip>
      )}
    </div>
  )
}
